import { supabase } from '../../../services/supabaseClient';

const TABLE = 'mf_imoveis';
const STORAGE_BUCKET = 'mf-imoveis';

// ===== APROVACAO DE IMOVEIS CADASTRADOS POR CORRETOR =====

export async function aprovarImovel(imovelId, aprovadoPor) {
  // Aprovar tambem publica automaticamente (evita 2 cliques separados pro admin)
  const { error } = await supabase
    .from(TABLE)
    .update({
      status_aprovacao: 'aprovado',
      status: 'publicado',
      motivo_rejeicao: null,
      aprovado_em: new Date().toISOString(),
      aprovado_por: aprovadoPor || null,
    })
    .eq('id', imovelId);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function rejeitarImovel(imovelId, motivo, aprovadoPor) {
  const { error } = await supabase
    .from(TABLE)
    .update({
      status_aprovacao: 'rejeitado',
      motivo_rejeicao: (motivo || '').trim() || 'Sem motivo informado',
      aprovado_em: new Date().toISOString(),
      aprovado_por: aprovadoPor || null,
    })
    .eq('id', imovelId);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

// ===== LISTAR IMOVEIS DO CORRETOR LOGADO =====

export async function listPropertiesByCorretor(corretorId) {
  if (!corretorId) return [];
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, titulo, tipo, status, status_aprovacao, motivo_rejeicao, preco, preco_aluguel, quartos, bairro, cidade, condominio, imagens, created_at, updated_at')
    .eq('corretor_id', corretorId)
    .order('updated_at', { ascending: false });
  if (error) {
    console.error('[listPropertiesByCorretor]', error);
    return [];
  }
  return data || [];
}

// ===== STORAGE =====

export function getImageUrl(path) {
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data?.publicUrl || '';
}

export function optimizedImageUrl(url, { width, quality = 75 } = {}) {
  if (!url || !width) return url || '';
  if (!/supabase\.co\/storage\/v1\/object\/public\//.test(url)) return url;
  const base = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/',
  );
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}width=${width}&quality=${quality}&resize=contain`;
}

export async function uploadImage(file, folder) {
  const fileName = `${folder}/${Date.now()}-${file.name}`;
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, file, { cacheControl: '31536000', upsert: false });

  if (error) {
    console.error('Erro ao subir imagem:', error);
    return null;
  }
  return getImageUrl(data.path);
}

// ===== VIDEO UPLOAD com compressão server-side =====
// Endpoint Bianca recebe vídeo do bucket _raw/ e devolve MP4 H.264 + faststart no destino final.
const BIANCA_API_BASE = 'https://api.abianca.com.br';
const BIANCA_API_TOKEN = 'QOQMRWtDQA9W8pluLNSV'; // token MF em BIANCA_CLIENTES
const HEAVY_VIDEO_EXTS = ['.mov', '.mkv', '.avi', '.webm', '.m4v'];
const VIDEO_COMPRESS_THRESHOLD = 10 * 1024 * 1024; // > 10MB sempre comprime
const VIDEO_MAX_SIZE = 500 * 1024 * 1024;          // > 500MB rejeita antes de subir

function shouldCompressVideo(file) {
  const ext = (file.name.toLowerCase().match(/\.[a-z0-9]+$/) || [''])[0];
  return HEAVY_VIDEO_EXTS.includes(ext) || file.size > VIDEO_COMPRESS_THRESHOLD;
}

export async function uploadVideo(file, folder, onStage) {
  if (file.size > VIDEO_MAX_SIZE) {
    throw new Error(`Vídeo muito grande (${Math.round(file.size / 1024 / 1024)}MB). Máximo permitido: 500MB.`);
  }
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^\w.-]+/g, '_');

  // MP4 pequeno → upload direto, mesmo caminho de antes
  if (!shouldCompressVideo(file)) {
    onStage?.('Enviando vídeo');
    const path = `${folder}/${timestamp}-${safeName}`;
    const { data, error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
      cacheControl: '31536000',
      upsert: false,
      contentType: file.type || 'video/mp4',
    });
    if (error) throw error;
    return getImageUrl(data.path);
  }

  // Caminho com compressão
  onStage?.('Enviando vídeo bruto');
  const rawPath = `_raw/${folder}/${timestamp}-${safeName}`;
  const { error: upErr } = await supabase.storage.from(STORAGE_BUCKET).upload(rawPath, file, {
    cacheControl: '31536000',
    upsert: false,
    contentType: file.type || 'application/octet-stream',
  });
  if (upErr) {
    // 413: limite do bucket no Supabase. Mensagem específica pra ajudar.
    if (upErr.statusCode === '413' || /maximum allowed size|exceeded/i.test(upErr.message || '')) {
      throw new Error(
        `Arquivo de ${Math.round(file.size / 1024 / 1024)}MB excede o limite do bucket no Supabase. ` +
        `Aumente o "File size limit" do bucket mf-imoveis no dashboard do Supabase.`
      );
    }
    throw upErr;
  }

  onStage?.('Preparando conversão');
  const trigRes = await fetch(`${BIANCA_API_BASE}/webhook/video/compress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Token': BIANCA_API_TOKEN },
    body: JSON.stringify({ bucket: STORAGE_BUCKET, path: rawPath, folder }),
  });
  if (!trigRes.ok) {
    const errText = await trigRes.text().catch(() => '');
    throw new Error(`Falha ao iniciar conversão: ${trigRes.status} ${errText}`);
  }
  const { job_id } = await trigRes.json();

  const startedAt = Date.now();
  const TIMEOUT_MS = 10 * 60 * 1000;
  let consecutiveErrors = 0;
  const MAX_CONSECUTIVE_ERRORS = 8; // ~20s de erros seguidos antes de desistir
  while (true) {
    if (Date.now() - startedAt > TIMEOUT_MS) {
      throw new Error('Tempo esgotado convertendo o vídeo (>10min).');
    }
    await new Promise(r => setTimeout(r, 2500));
    try {
      const statusRes = await fetch(`${BIANCA_API_BASE}/webhook/video/compress/${job_id}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
        headers: { 'X-Token': BIANCA_API_TOKEN },
      });
      if (!statusRes.ok) {
        // 5xx/CORS transitório — não mata o upload, o job continua vivo no backend
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw new Error(`Erro ao verificar status: ${statusRes.status} (${consecutiveErrors} tentativas)`);
        }
        continue;
      }
      consecutiveErrors = 0;
      const job = await statusRes.json();
      // Backend reporta progresso em marcos: 0 → 5 → 20 → 85 → 100.
      // Entre 20 e 85 o ffmpeg roda como bloco único — mostra mensagem clara em vez de % parado.
      const p = job.progress || 0;
      const elapsedSec = Math.round((Date.now() - startedAt) / 1000);
      let label;
      if (p < 20) label = 'Preparando vídeo no servidor';
      else if (p < 85) label = `Comprimindo vídeo... (${elapsedSec}s)`;
      else if (p < 100) label = 'Finalizando upload';
      else label = 'Concluindo';
      onStage?.(label);
      if (job.status === 'done') return job.output_url;
      if (job.status === 'error') throw new Error(`Conversão falhou: ${job.error || 'erro desconhecido'}`);
    } catch (err) {
      // TypeError: Failed to fetch (CORS, network) — também é transitório
      if (err instanceof TypeError) {
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw new Error(`Conexão instável com o servidor de conversão (${consecutiveErrors} falhas).`);
        }
        continue;
      }
      throw err;
    }
  }
}

// ===== READ =====

export async function getPublishedProperties(filters = {}) {
  let query = supabase
    .from(TABLE)
    .select('*')
    .eq('status', 'publicado')
    .eq('status_aprovacao', 'aprovado')
    .order('destaque', { ascending: false })
    .order('created_at', { ascending: false });

  if (filters.busca) {
    const term = `%${filters.busca.trim()}%`;
    query = query.or(`titulo.ilike.${term},endereco.ilike.${term},condominio.ilike.${term},cidade.ilike.${term}`);
  }
  if (filters.cidade) query = query.eq('cidade', filters.cidade);
  if (filters.condominio) query = query.ilike('condominio', filters.condominio);
  if (filters.tipo) query = query.eq('tipo', filters.tipo);
  // tipo_negocio agora é derivado: tem preco>0 = venda, tem preco_aluguel>0 = locação
  if (filters.tipo_negocio === 'locacao') {
    query = query.gt('preco_aluguel', 0);
  } else if (filters.tipo_negocio === 'venda') {
    query = query.gt('preco', 0);
  }
  if (filters.quartos_min) query = query.gte('quartos', filters.quartos_min);
  // Faixa de preço: usa coluna correta conforme tipo_negocio
  const precoCol = filters.tipo_negocio === 'locacao' ? 'preco_aluguel' : 'preco';
  if (filters.preco_max) query = query.lte(precoCol, filters.preco_max);
  if (filters.preco_min) query = query.gte(precoCol, filters.preco_min);

  const { data, error } = await query;
  if (error) {
    console.error('Erro ao buscar imoveis:', error);
    return [];
  }
  return data;
}

export async function getFilterOptions() {
  const { data, error } = await supabase
    .from(TABLE)
    .select('bairro, cidade, condominio, tipo, preco, preco_aluguel')
    .eq('status', 'publicado');

  if (error) {
    console.error('Erro ao buscar opcoes de filtro:', error);
    return { bairros: [], cidades: [], condominiosByCity: {}, tipos: [], precosVenda: [], precosAluguel: [] };
  }

  const bairros = [...new Set(data.map(d => d.bairro).filter(Boolean))].sort();
  const cidades = [...new Set(data.map(d => d.cidade).filter(Boolean))].sort();
  const tipos = [...new Set(data.map(d => d.tipo).filter(Boolean))].sort();

  // Agrupa condomínios por cidade (dedup case-insensitive + trim + sort)
  // Mantem a primeira grafia encontrada como label canonico (evita "Gran Alphaville",
  // "GRAN ALPHAVILLE" e "gran alphaville" virarem 3 entradas separadas)
  const condominiosByCity = {};
  for (const row of data) {
    if (!row.cidade || !row.condominio) continue;
    const cleaned = String(row.condominio).trim();
    if (!cleaned) continue;
    const key = cleaned.toLowerCase();
    if (!condominiosByCity[row.cidade]) condominiosByCity[row.cidade] = new Map();
    if (!condominiosByCity[row.cidade].has(key)) {
      condominiosByCity[row.cidade].set(key, cleaned);
    }
  }
  for (const city of Object.keys(condominiosByCity)) {
    condominiosByCity[city] = [...condominiosByCity[city].values()].sort((a, b) =>
      a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
    );
  }

  const precosVenda = data.map(d => Number(d.preco)).filter(n => n > 0);
  const precosAluguel = data.map(d => Number(d.preco_aluguel)).filter(n => n > 0);

  return { bairros, cidades, condominiosByCity, tipos, precosVenda, precosAluguel };
}

export async function getFeaturedProperties() {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('status', 'publicado')
    .eq('destaque', true)
    .order('created_at', { ascending: false })
    .limit(6);

  if (error) {
    console.error('Erro ao buscar destaques:', error);
    return [];
  }
  return data;
}

// Aceita UUID puro OU slug terminado em "...-xxxxxxxx" (8 primeiros chars do UUID)
export async function getPropertyById(idOrSlug) {
  if (!idOrSlug) return null;
  const uuidShort = extractUuidShort(idOrSlug);

  // UUID completo (36 chars com hífens) — busca direta
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug)) {
    const { data, error } = await supabase
      .from(TABLE).select('*').eq('id', idOrSlug).single();
    if (error) { console.error('Erro ao buscar imovel:', error); return null; }
    return data;
  }

  // Slug com sufixo de 8 chars do UUID — busca por range (LIKE não funciona em uuid)
  if (uuidShort) {
    const start = `${uuidShort}-0000-0000-0000-000000000000`;
    const end = `${uuidShort}-ffff-ffff-ffff-ffffffffffff`;
    const { data, error } = await supabase
      .from(TABLE).select('*').gte('id', start).lte('id', end).limit(1);
    if (error) { console.error('Erro ao buscar imovel por slug:', error); return null; }
    return data?.[0] || null;
  }

  return null;
}

// Extrai os últimos 8 chars hex de um slug "casa-3-quartos-alphaville-7bf3195d"
function extractUuidShort(slug) {
  const m = slug.match(/-([0-9a-f]{8})$/i);
  return m ? m[1] : null;
}

// Slug SEO-friendly + sufixo do UUID pra unicidade
// Ex: "casa-serra-da-estrela-aracariguama-7bf3195d"
export function buildPropertySlug(property) {
  if (!property?.id) return '';
  const uuidShort = property.id.split('-')[0]; // 8 primeiros chars
  const parts = [];
  if (property.tipo) parts.push(property.tipo);
  if (property.quartos) parts.push(`${property.quartos}-quartos`);
  if (property.bairro) parts.push(property.bairro);
  else if (property.condominio) parts.push(property.condominio);
  if (property.cidade) parts.push(property.cidade);

  const slugBase = parts
    .join(' ')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // remove acentos
    .replace(/[^a-z0-9\s-]/g, '')                      // só alfanumérico/espaço/hífen
    .trim()
    .replace(/\s+/g, '-')                              // espaços -> hífen
    .replace(/-+/g, '-');                              // colapsa hífens

  return slugBase ? `${slugBase}-${uuidShort}` : uuidShort;
}

// ===== WRITE =====

export async function createProperty(propertyData) {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(propertyData)
    .select()
    .single();

  if (error) {
    console.error('Erro ao criar imovel:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

export async function updateProperty(id, updates) {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Erro ao atualizar imovel:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

export async function publishProperty(id) {
  return updateProperty(id, { status: 'publicado' });
}

// ===== GEOCODING =====

/**
 * Dispara a task geocode-imovel do trigger.dev via /api/blog-trigger.
 * A task busca coordenadas reais no Google Maps e grava em mf_imoveis.
 * Eh fire-and-forget — roda em background apos save do imovel no admin.
 */
export async function triggerGeocodeImovel(imovelId) {
  try {
    const res = await fetch('/api/blog-trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        task: 'geocode-imovel',
        payload: { imovel_id: imovelId },
      }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data, status: res.status };
  } catch (err) {
    console.error('Erro ao disparar geocode-imovel:', err);
    return { ok: false, error: String(err) };
  }
}

// ===== HELPERS =====

export function formatPrice(value) {
  if (!value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPriceOrConsult(value) {
  return value ? formatPrice(value) : 'Sob consulta';
}

export function formatPublishedAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return 'Publicado hoje';
  if (diffDays === 1) return 'Publicado ontem';
  if (diffDays < 7) return `Publicado há ${diffDays} dias`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `Publicado há ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `Publicado há ${months} ${months === 1 ? 'mês' : 'meses'}`;
  }
  const years = Math.floor(diffDays / 365);
  return `Publicado há ${years} ${years === 1 ? 'ano' : 'anos'}`;
}

export function formatArea(m2) {
  if (!m2) return '';
  return `${m2} m²`;
}

export function buildSpecsText(property) {
  const parts = [];
  if (property.area_m2) parts.push(`${property.area_m2} m²`);
  if (property.quartos) parts.push(`${property.quartos} quarto${property.quartos > 1 ? 's' : ''}`);
  if (property.banheiros) parts.push(`${property.banheiros} banheiro${property.banheiros > 1 ? 's' : ''}`);
  if (property.vagas) parts.push(`${property.vagas} vaga${property.vagas > 1 ? 's' : ''}`);
  return parts;
}

export function buildSpecsStructured(property) {
  const specs = [];
  if (property.area_m2) specs.push({ type: 'area', value: property.area_m2, unit: 'm²' });
  if (property.quartos) specs.push({ type: 'quartos', value: property.quartos });
  if (property.banheiros) specs.push({ type: 'banheiros', value: property.banheiros });
  if (property.vagas) specs.push({ type: 'vagas', value: property.vagas });
  return specs;
}

// ===== SEO =====

const TIPO_LABELS = {
  apartamento: 'Apartamento',
  casa: 'Casa',
  cobertura: 'Cobertura',
  terreno: 'Terreno',
  studio: 'Studio',
  comercial: 'Imóvel Comercial',
};

function tipoLabel(tipo) {
  return TIPO_LABELS[tipo] || 'Imóvel';
}

// Decide o "verbo" baseado em quais preços estão preenchidos
function negocioLabel(p) {
  const hasV = Number(p.preco) > 0;
  const hasA = Number(p.preco_aluguel) > 0;
  if (hasV && hasA) return 'Venda e Locação';
  if (hasA) return 'Locação';
  return 'Venda'; // default
}

export function generateSeoTitle(property) {
  if (!property) return '';
  const tipo = tipoLabel(property.tipo);
  const negocio = negocioLabel(property);
  const local = property.bairro || property.cidade || '';
  const localTxt = local ? ` em ${local}` : '';

  const specs = [];
  if (property.quartos) specs.push(`${property.quartos} quartos`);
  if (property.area_m2) specs.push(`${property.area_m2}m²`);
  const specsTxt = specs.length ? ` · ${specs.join(', ')}` : '';

  return `${tipo} à ${negocio}${localTxt}${specsTxt} | MF Real Estate`;
}

export function generateSeoDescription(property) {
  if (!property) return '';
  const tipo = tipoLabel(property.tipo);
  const negocio = negocioLabel(property).toLowerCase();
  const local = [property.bairro, property.cidade].filter(Boolean).join(', ');
  const specs = buildSpecsText(property).join(' · ');

  const precoV = Number(property.preco) > 0
    ? `Venda ${formatPrice(property.preco)}`
    : '';
  const precoA = Number(property.preco_aluguel) > 0
    ? `Aluguel ${formatPrice(property.preco_aluguel)}/mês`
    : '';
  const valores = [precoV, precoA].filter(Boolean).join(' · ');

  const partes = [
    `${tipo} para ${negocio}${local ? ` em ${local}` : ''}`,
    specs,
    valores,
    'Agende uma visita com a MF Real Estate.',
  ].filter(Boolean);

  return partes.join('. ').replace(/\.\./g, '.');
}

export function generateSeoKeywords(property) {
  if (!property) return '';
  const tipo = tipoLabel(property.tipo).toLowerCase();
  const out = new Set();
  out.add(tipo);
  out.add(`${tipo} à venda`);
  out.add(`${tipo} para alugar`);
  if (property.bairro) {
    out.add(`${tipo} ${property.bairro.toLowerCase()}`);
    out.add(`imóveis ${property.bairro.toLowerCase()}`);
  }
  if (property.cidade) {
    out.add(`${tipo} ${property.cidade.toLowerCase()}`);
  }
  if (property.condominio) {
    out.add(property.condominio.toLowerCase());
  }
  if (property.quartos) {
    out.add(`${tipo} ${property.quartos} quartos`);
  }
  return [...out].slice(0, 10).join(', ');
}
